import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { scrollToId } from "@/hooks/useLenis";

const links = [
  { label: "Inicio", id: "inicio" },
  { label: "Nosotros", id: "nosotros" },
  { label: "Servicios", id: "servicios" },
  { label: "Formalización", id: "formalizacion" },
  { label: "Suscripción", id: "planes" },
  { label: "Acceso a Sistemas", id: "plataformas" },
  { label: "Contacto", id: "contacto" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("inicio");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -45% 0px" }
    );
    links.forEach((l) => {
      const el = document.getElementById(l.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-vnavy/80 backdrop-blur-xl border-b border-white/10 py-3" : "py-6 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("inicio")}
          className="group"
        >
          <span className="inline-flex items-center bg-white rounded-xl px-4 py-2.5 shadow-lg shadow-black/10 transition-transform duration-300 group-hover:scale-[1.03]">
            <img src="/logo-vertice-h.webp" alt="Grupo Vértice CR" className="h-10 sm:h-12 w-auto object-contain" />
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`link-underline text-sm font-medium transition-colors ${
                active === l.id ? "active text-vteal-light" : "text-gray-300 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-admin-btn"
            onClick={() => navigate("/admin/login")}
            className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-gray-300 hover:text-vteal-light transition-colors"
          >
            <Lock className="h-3.5 w-3.5" /> Admin
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-vnavy/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => go(l.id)}
                  className={`text-left text-base font-medium transition-colors ${
                    active === l.id ? "text-vteal-light" : "text-gray-200"
                  }`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => navigate("/admin/login")}
                className="text-left text-sm uppercase tracking-widest text-vteal-light font-semibold"
              >
                Acceso Administrador
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
